import { Form, Input, InputNumber, Select, Switch } from "antd";

import { TAG_SCREEN_HELP_TEXT } from "../helper";

/**
 * Renders one input per field the chosen product line adds to a mapping.
 *
 * The field list comes from the backend, which reads it off that product line's
 * own schema, so this component never learns what any product line records: a
 * line that gains a field gains an input for it with no change here.
 *
 * Meant to sit inside an existing `Form`; each field is keyed by its own name,
 * so the form's values carry the metadata object directly.
 *
 * @param {object} props
 * @param {Array<{name: string, label: string, kind: string, options: Array<string>}>} props.metadataFields
 * @param {boolean} [props.isDisabled=false]
 */
const MetadataFields = ({ metadataFields, isDisabled = false }) => (
  <>
    {metadataFields.map(({ name, label, kind, options }) => (
      <Form.Item
        key={name}
        name={name}
        label={label}
        extra={TAG_SCREEN_HELP_TEXT.metadata}
        valuePropName={kind === "boolean" ? "checked" : "value"}
      >
        {renderMetadataControl({ kind, options, label, isDisabled })}
      </Form.Item>
    ))}
  </>
);

/**
 * The input one metadata field renders as.
 *
 * @param {object} params
 * @param {string} params.kind - `select`, `number`, `boolean`, `date` or `text`.
 * @param {Array<string>} params.options - Allowed values, non-empty only for `select`.
 * @param {string} params.label
 * @param {boolean} params.isDisabled
 * @returns {import("react").ReactElement}
 */
const renderMetadataControl = ({ kind, options, label, isDisabled }) => {
  if (kind === "select") {
    return (
      <Select
        allowClear
        disabled={isDisabled}
        placeholder={label}
        options={options.map((option) => ({ label: option, value: option }))}
      />
    );
  }
  if (kind === "number") {
    return <InputNumber style={{ width: "100%" }} disabled={isDisabled} placeholder={label} />;
  }
  if (kind === "boolean") {
    return <Switch disabled={isDisabled} />;
  }

  return <Input disabled={isDisabled} placeholder={label} maxLength={200} />;
};

export default MetadataFields;
